import { useId, useState } from "react";
import type { ServedAction } from "@patent-studio/api-types";
import { Button } from "@/components/ui/button";
import { FieldLabel } from "@/components/ui/FieldLabel";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { Note } from "@/components/ui/Note";
import { refusalDetail } from "@/services/api-client";
import { useUnsavedDraft } from "@/hooks/use-unsaved-draft";
import { Panel } from "@/components/ui/Panel";
import { Textarea } from "@/components/ui/textarea";

/**
 * A text with its marked runs drawn as `del` or `ins`: the words a claim's
 * rewording takes out of the Before or puts into the After, so no word of the
 * claim changes unseen.
 */
function Marked({
  text,
  marks,
  as: Run,
}: {
  text: string;
  marks: ServedAction["after_marks"];
  as: "del" | "ins";
}) {
  const runs = [];
  let from = 0;
  for (const mark of marks) {
    runs.push(text.slice(from, mark.start));
    runs.push(
      <Run
        key={mark.start}
        className={
          Run === "ins"
            ? "rounded-[3px] bg-primary/10 px-0.5 font-semibold text-foreground no-underline"
            : "text-foreground line-through decoration-destructive/70"
        }
      >
        {text.slice(mark.start, mark.end)}
      </Run>,
    );
    from = mark.end;
  }
  runs.push(text.slice(from));
  return <span>{runs}</span>;
}

/**
 * The claim's new words with the clause marked, as v0.28 draws a claim's After,
 * or with each run a rewording puts in marked. Any other change shows its After
 * as it reads.
 */
function After({ change }: { change: ServedAction }) {
  const after = change.after ?? "";
  if (change.after_marks.length > 0)
    return <Marked text={after} marks={change.after_marks} as="ins" />;
  const at =
    change.target_kind === "claim" ? after.lastIndexOf(change.amend_text) : -1;
  if (at < 0) return <span>{after}</span>;
  return (
    <span>
      {after.slice(0, at)}
      <mark className="rounded-[3px] bg-primary/10 px-0.5 font-semibold text-foreground">
        {change.amend_text}
      </mark>
      {after.slice(at + change.amend_text.length)}
    </span>
  );
}

/** Before and After, for a change to a record that stands; an addition has no Before. */
function Diff({ change }: { change: ServedAction }) {
  return (
    <dl className="grid grid-cols-[52px_minmax(0,1fr)] gap-x-2.5 gap-y-1.5 text-[12.5px] leading-relaxed">
      <MicroLabel as="dt" size="sm" className="pt-[3px]">
        Before
      </MicroLabel>
      {change.before_marks.length > 0 ? (
        <dd className="text-muted-foreground">
          <Marked
            text={change.before ?? ""}
            marks={change.before_marks}
            as="del"
          />
        </dd>
      ) : (
        <dd className="text-muted-foreground line-through decoration-muted-foreground/45">
          {change.before}
        </dd>
      )}
      <MicroLabel as="dt" size="sm" className="pt-[3px] text-primary">
        After
      </MicroLabel>
      <dd>
        <After change={change} />
      </dd>
    </dl>
  );
}

const NOT_COMMITTED = "The amendment was not saved.";

/** The attorney's version: the one text v0.28 lets them edit, labelled as it labels it. */
function AmendField({
  change,
  pending,
  onCommit,
  onCancel,
}: {
  change: ServedAction;
  pending: boolean;
  onCommit: (text: string) => Promise<unknown>;
  onCancel: () => void;
}) {
  const id = useId();
  const [text, setText] = useState(change.amend_text);
  const [refusal, setRefusal] = useState<string | null>(null);
  // A refused amendment lives only here, so it counts as unsaved until it lands or is cancelled.
  useUnsavedDraft(refusal !== null);
  const said = text.trim();
  const commit = () => {
    setRefusal(null);
    onCommit(said).catch((failure: unknown) => {
      setRefusal(refusalDetail(failure, NOT_COMMITTED));
    });
  };
  return (
    <div className="flex flex-col gap-1.5">
      <FieldLabel htmlFor={id}>{change.amend_label}</FieldLabel>
      <Textarea
        id={id}
        rows={2}
        value={text}
        onChange={(event) => {
          setText(event.target.value);
        }}
      />
      <div className="flex flex-wrap gap-2">
        <Button size="dense" disabled={pending || said === ""} onClick={commit}>
          Commit amendment
        </Button>
        <Button variant="secondary" size="dense" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      {refusal !== null && (
        <Note tone="destructive" role="alert">
          {`${refusal} Your words are kept here: commit them again.`}
        </Note>
      )}
    </div>
  );
}

/**
 * One suggested change on its record, as v0.28 draws it: a red mark and
 * "Suggested addition" or "Suggested change", the reason, Before and After for
 * a change to a record that stands, then Accept, Amend and Reject.
 */
export function SuggestionPanel({
  change,
  pending,
  accept,
  amend,
  reject,
}: {
  change: ServedAction;
  pending: boolean;
  accept: () => void;
  amend: (text: string) => Promise<unknown>;
  reject: () => void;
}) {
  const id = useId();
  const [amending, setAmending] = useState(false);
  return (
    <Panel
      role="group"
      aria-labelledby={id}
      tone="brand"
      pad="snug"
      className="mt-3 flex flex-col gap-2.5 text-left text-foreground"
    >
      <span className="flex items-center gap-[7px]">
        <span
          aria-hidden
          className="size-[7px] flex-none rounded-full bg-destructive"
        />
        <MicroLabel id={id} size="sm" className="text-primary">
          {change.heading}
        </MicroLabel>
      </span>
      <p className="text-[12.5px] leading-relaxed">{change.reason}</p>
      {change.change === "modify" && <Diff change={change} />}
      {amending ? (
        <AmendField
          change={change}
          pending={pending}
          onCommit={amend}
          onCancel={() => {
            setAmending(false);
          }}
        />
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button size="dense" disabled={pending} onClick={accept}>
            Accept
          </Button>
          <Button
            variant="secondary"
            size="dense"
            onClick={() => {
              setAmending(true);
            }}
          >
            Amend
          </Button>
          <Button
            variant="danger-outline"
            size="dense"
            disabled={pending}
            onClick={reject}
          >
            Reject
          </Button>
        </div>
      )}
    </Panel>
  );
}
